import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Video, GraduationCap, Users, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MeetingInvitePanel } from "@/components/meeting/MeetingInvitePanel";
import { LiveKitMeetingRoom } from "@/components/meeting/livekit/LiveKitMeetingRoom";

export default function MeetingRoomPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    api.get(`/meetings/${roomId}`).then(({ data }) => setMeeting(data)).catch((e) => {
      setLoadError(true);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Meeting not found");
    });
  }, [roomId]);

  const leave = () => { setJoined(false); navigate("/meetings"); };

  if (loadError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-6" data-testid="meeting-room-error">
        <p className="text-slate-300">This meeting could not be found.</p>
        <button onClick={() => navigate("/meetings")} className="text-cyan-400 hover:text-cyan-300 text-sm">Back to Meetings</button>
      </div>
    );
  }

  if (!meeting) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  }

  const isHost = meeting.host_id === user?.user_id;

  if (joined) {
    return <LiveKitMeetingRoom meetingId={roomId} meeting={meeting} isHost={isHost} onLeft={leave} />;
  }

  return (
    <div className="max-w-lg mx-auto px-6 py-16" data-testid="meeting-lobby">
      <button onClick={() => navigate("/meetings")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="glass-panel rounded-2xl p-8 text-center">
        {meeting.meeting_type === "class" ? <GraduationCap className="w-10 h-10 text-fuchsia-400 mx-auto mb-3" /> : <Users className="w-10 h-10 text-cyan-400 mx-auto mb-3" />}
        <h1 className="font-heading text-2xl font-semibold text-white">{meeting.title}</h1>
        <p className="text-sm text-slate-400 mt-2">
          {meeting.meeting_type === "class"
            ? "Live cohort class — the host broadcasts video, you can raise your hand to speak."
            : "1-on-1 interview call — everyone joins on camera."}
        </p>
        <p className="text-xs text-slate-500 mt-2">Hosted by {meeting.host_name} · Code: <span className="font-mono text-slate-300">{meeting.join_code}</span></p>
        {meeting.waiting_room_enabled && !isHost && (
          <p className="text-xs text-amber-300 mt-1">This meeting has a Waiting Room — the host will need to admit you.</p>
        )}
        <button onClick={() => setJoined(true)} data-testid="meeting-join-room-btn"
          className="mt-6 w-full flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-500/90 text-white text-sm font-semibold px-5 py-3 rounded-lg transition-colors">
          <Video className="w-4 h-4" /> Join Now
        </button>
      </div>
      {isHost && <div className="mt-6"><MeetingInvitePanel meetingId={roomId} joinCode={meeting.join_code} /></div>}
    </div>
  );
}
